import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Form from "@/components/ui/Form";
import ViewWrapper from "../../components/ui/ViewWrapper";
import { useToast } from "@/hooks/useToast";
import { SupabaseInstance } from "@/integrations/supabase";
import { UpdatePasswordSchema, type UpdatePasswordType } from "./form";
import { TOAST_MESSAGES } from "./constants";

const REPEATED_PASSWORD_RESPONSE_ERROR =
  "New password should be different from the old password";

export default function UpdatePasswordPage() {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<UpdatePasswordType>({
    resolver: zodResolver(UpdatePasswordSchema),
  });
  const onSubmit = handleSubmit((data) => {
    setIsLoading(true);
    SupabaseInstance.updatePassword(data.password)
      .then(({ error }) => {
        if (error) {
          if (
            error?.status === 422 ||
            error?.message.includes(REPEATED_PASSWORD_RESPONSE_ERROR)
          ) {
            handleShowToast("REPEATED_PASSWORD");
            return;
          }
          handleShowToast("GENERIC_ERROR");
          return;
        }
        handleShowToast("SUCCESS");
      })
      .catch((_) => {
        handleShowToast("SERVER_ERROR");
      })
      .finally(() => {
        setIsLoading(false);
      });
  });

  const handleShowToast = useCallback(
    (type: keyof typeof TOAST_MESSAGES) => {
      toast(TOAST_MESSAGES[type]);
    },
    [toast]
  );

  return (
    <ViewWrapper>
      <div className="mx-auto p-6 bg-white rounded-lg shadow-md border border-gray-200">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-startfly-black mb-2">
            Cambiar la contraseña
          </h1>
        </div>
        <Form.ErrorView
          isVisible={!!(errors.password || errors.confirmPassword)}
          title={errors.password?.message || errors.confirmPassword?.message}
        />
        <Form.Container onSubmit={onSubmit}>
          <Form.Field
            {...register("password")}
            label="Contraseña"
            labelHtmlFor="password"
            id="password"
            type="password"
            placeholder="••••••••"
            error={errors.hasOwnProperty("password")}
          />
          <Form.Field
            {...register("confirmPassword")}
            label="Confirmar Contraseña"
            labelHtmlFor="confirmPassword"
            id="confirmPassword"
            type="password"
            placeholder="••••••••"
            error={errors.hasOwnProperty("confirmPassword")}
          />
          <Form.SubmitButton
            isLoading={isLoading}
            title="Cambiar la contraseña"
            loadingText="Reiniciando la contraseña..."
          />
        </Form.Container>
      </div>
    </ViewWrapper>
  );
}
